import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { localize } from 'src/utils/l10n';
import FormSteps from '../FormSteps/FormSteps';
import { connect, useDispatch } from 'react-redux';
import FormStep from '../FormSteps/FormStep';
import FormSection from '../FormSection/FormSection';
import FormControl from '../FormControl/FormControl';
import { ChoiceGroup, DatePicker, DayOfWeek, DefaultButton, Dropdown, PrimaryButton, SpinButton, Stack, TextField, TimePicker } from '@fluentui/react';
import { uniqueId, validateRequiredTextField } from 'src/utils';
import './ProjectForm.scss';
import api from 'src/api/SpintrApi';
import { AxiosResponse } from 'axios';
import UnstyledButton from '../UnstyledButton/UnstyledButton';
import Visage2Icon from '../Visage2Icon/Visage2Icon';
import Label from '../Label/Label';
import Loader from '../Loader/Loader';
import ErrorMessagebar from '../Messagebars/ErrorMessagebar';
import { setConfirmPopup } from 'src/actions/popup';
import getDatePickerLanguageStrings from 'src/utils/getDatePickerLanguageStrings';
import formatDatePickerDate from 'src/utils/formatDatePickerDate';
import { fetchProjects } from 'src/actions/project';
import PopupHeader from '../PopupHeader/PopupHeader';
import ProjectStatusSelector from '../ProjectStatusSelector/ProjectStatusSelector';
import { IApplicationState } from 'src/reducer';
import FormTokenizedObjectInput from '../FormTokenizedObjectInput/FormTokenizedObjectInput';
import PhoenixForm from '../PhoenixForm/PhoenixForm';
import onFormClose from 'src/utils/onFormClose';
import AnimatedDots from '../AnimatedDots/AnimatedDots';
import { SpintrTypes } from 'src/typings';
import VectorForm from './VectorForm';
import CompactTable from '../CompactTable/CompactTable';
import QuestionForm from './QuestionForm';

interface IProps {
    id?: number;
    instanceId?: number;
    chooseInstance?: boolean;
    instancePro?: boolean;
    onClose: () => void;
}

export interface IFormStep {
    title: string;
    cancelButtonText: string;
    saveButtonText: string;
}

export interface IProject {
    id: number;
    name: string;
    startDate: Date;
    endDate?: Date;
    purpose: string;
    goal: string;
    isStrategicGoal: boolean;
    strategicGoal?: string;
    strategicGoalChallenges?: string;
    surveyInterval: number;
    surveyIntervalFreeChoiceDays: number;
    baselineDate?: Date,
    questionModelling: number;
    vectors: any;
    status: number;
    instanceId?: number;
    instance?: any;
    isAnonymous: boolean;
    hasEndQuestion: boolean;
    endQuestionText?: string;
    hasENPSQuestion: boolean;
    targetGroups: any[];
    template?: number;
}

interface IProjectTemplate {
    id: number;
    title: string;
    description: string;
    icon: string;
    iconColor: string;
    backgroundColor: string;
    buttonText: string,
    options: Partial<IProject>;
}

interface IState {
    step: number;
    item: IProject;
    projectTypes: any[];
    activeVectorIndex: number;
    activeQuestionIndex: number;
    displayVectorForm: boolean;
    isCreatingVector: boolean;
    isLoading: boolean;
    saveError: any[];
    isNew: boolean;
    pro: boolean;
    question?: any;
}

const removeItemAtIndex = (items: any[], index: number) => {
    const result = [...items];

    result.splice(index, 1);

    return result;
}

const ProjectForm = (props: IProps) => {
    const dispatch = useDispatch();

    const isNew = !props.id || props.id === 0;

    const [state, setState] = useState<IState>({
        step: 0,
        item: {
            id: 0,
            name: "",
            startDate: new Date(),
            purpose: "",
            goal: "",
            isStrategicGoal: false,
            surveyInterval: 0,
            surveyIntervalFreeChoiceDays: 7,
            questionModelling: 0,
            vectors: [],
            status: 0,
            isAnonymous: false,
            hasEndQuestion: false,
            hasENPSQuestion: false,
            targetGroups: []
        },
        projectTypes: [],
        activeVectorIndex: -1,
        activeQuestionIndex: -1,
        displayVectorForm: false,
        isCreatingVector: false,
        isLoading: !isNew,
        saveError: [],
        isNew: isNew,
        pro: props.chooseInstance ? false : props.instancePro,
    });

    const steps : IFormStep[] = useMemo(() => {
        return [{
            title: localize("PROJECT_INFORMATION"),
            cancelButtonText: localize("Avbryt"),
            saveButtonText: localize("NEXT")
        }, {
            title: state.item.template === 2 ? localize("QUESTIONS") : isNew ? localize("PROJECT_TYPE_AND_VECTORS") : localize("VECTORS"),
            cancelButtonText: localize("PREVIOUS"),
            saveButtonText: localize("NEXT")
        }, {
            title: localize("Installningar"),
            cancelButtonText: localize("PREVIOUS"),
            saveButtonText: isNew ? localize("LAUNCH_PROJECT") : localize("SAVE")
        }];
    }, [state.item.template]);

    useEffect(() => {
        api.get("/api/v1/projects/projecttypes?includeQuestions=true&translate=true&take=1000").then((response: AxiosResponse) => {
            setState(s => ({
                ...s,
                projectTypes: response.data.map((item: any) => {
                    return {
                        ...item,
                        vectors: item.vectors.map((vector: any) => {
                            return {
                                ...vector,
                                questionStatus: 3
                            }
                        })
                    }
                })
            }));
        }).catch(() => {});

        if (!state.isNew) {
            api.get("/api/v1/projects/" + props.id, {
                params: {
                    instanceId: props.instanceId,
                },
            }).then((response: AxiosResponse) => {
                setState(s => ({
                    ...s,
                    isLoading: false,
                    item: {
                        ...response.data,
                        startDate: new Date(response.data.startDate),
                        endDate: response.data.endDate ? new Date(response.data.endDate) : undefined,
                        baselineDate: new Date(response.data.baselineDate),
                        vectors: response.data.vectors.map((v: any) => {
                            return {
                                ...v,
                                questionStatus: 3
                            }
                        })
                    }
                }));
            }).catch(() => {});
        }
    }, []);

    useEffect(() => {
        if (!state.item.instanceId || !props.chooseInstance) {
            return;
        }

        api.get("/api/v1/instances/" + state.item.instanceId).then((response) => {
            setState(s => ({
                ...s,
                pro: response.data.pro,
            }));
        }).catch(() => {});
    }, [props.chooseInstance, state.item.instanceId]);

    const validateStep = (step: number) => {
        let isOk = true;

        if (step === 0) {
            let requiredFields = [
                state.item.name
            ];

            if (state.item.template === 1) {
                requiredFields = [
                    ...requiredFields,
                    state.item.goal,
                    state.item.purpose
                ];

                if (state.item.isStrategicGoal) {
                    requiredFields = [
                        ...requiredFields,
                        state.item.strategicGoal,
                        state.item.strategicGoalChallenges
                    ]
                }
            }

            if (props.chooseInstance) {
                requiredFields = [
                    ...requiredFields,
                    state.item.instance
                ]
            }

            if (!requiredFields.every(x => !!x)) {
                setState(s => ({
                    ...s,
                    saveError: ["VALIDATION_FILL_REQUIRED_FIELDS"]
                }));

                isOk = false;
            }
        } else if (step === 1) {
            const errors : string[] = [];

            if (state.item.template !== 2) {
                if (state.item.vectors.length < 3) {
                    errors.push("VALIDATION_VECTOR_COUNT");
                }
    
                if (!!state.item.vectors.find((x: any) => x.questions.length < 1)) {
                    errors.push("VALIDATION_QUESTION_COUNT");
                }
            }

            if (errors.length > 0) {
                setState(s => ({
                    ...s,
                    saveError: errors
                }));

                isOk = false;
            }
        }

        return isOk;
    }

    const close = useCallback(() => {
        onFormClose(dispatch, () => {
            props.onClose();
        });
    }, []);

    const save = useCallback(() => {
        setState(s => ({
            ...s,
            isLoading: true
        }));

        const step0Ok = validateStep(0);
        const step1Ok = validateStep(1);

        if (!step0Ok || !step1Ok) {
            setState(s => ({
                ...s,
                isLoading: false,
                step: step0Ok ? 0 : 1
            }));
        }

        api.post("/api/v1/projects", state.item).then((response: AxiosResponse) => {
            dispatch(fetchProjects());

            api.post("/api/v1/language/translate").then(() => {}).catch(() => {});

            props.onClose();
        }).catch(() => {
            setState(s => ({
                ...s,
                isLoading: false,
                saveError: ["TeknisktFel"]
            }));
        })
    }, [state.item]);

    const generateQuestions = useCallback(() => {
        for (let i = 0; i < state.item.vectors.length; i++) {
            const v = state.item.vectors[i];

            if (v.questionStatus > 2) {
                continue;
            }

            const envelope = {
                projectName: state.item.name,
                projectPurpose: state.item.purpose,
                projectGoal: state.item.goal,
                strategicGoal: state.item.isStrategicGoal ?
                    state.item.strategicGoal :
                    "",
                strategicGoalChallenges: state.item.isStrategicGoal ?
                    state.item.strategicGoalChallenges :
                    "",
                vectors: [v.name]
            };

            setState(s => ({
                ...s,
                item: {
                    ...s.item,
                    vectors: s.item.vectors.map((x: any, index: number) => {
                        if (index === i) {
                            return {
                                ...x,
                                questionStatus: 2
                            }
                        }
                        
                        return x;
                    })
                }
            }));

            api.post("/api/v1/projects/generatequestions", envelope).then((response: AxiosResponse) => {
                setState(s => ({
                    ...s,
                    item: {
                        ...s.item,
                        vectors: s.item.vectors.map((x: any, index: number) => {
                            if (index === i) {
                                return {
                                    ...x,
                                    questionStatus: 3,
                                    questions: [
                                        ...x.questions,
                                        ...response.data[0].questions.map((text: string) => {
                                            return {
                                                text
                                            }
                                        })
                                    ]
                                }
                            }
                            
                            return x;
                        })
                    }
                }));
            }).catch(() => {
                setState(s => ({
                    ...s,
                    item: {
                        ...s.item,
                        vectors: s.item.vectors.map((x: any, index: number) => {
                            if (index === i) {
                                return {
                                    ...x,
                                    questionStatus: 0
                                }
                            }
                            
                            return x;
                        })
                    }
                }));
            });
        }
    }, [state.item]);

    const isGeneratingQuestions = !!state.item.vectors.find(x => x.questionStatus === 2);

    const getProjectType = () => {
        for (let pt of state.projectTypes) {
            if (state.item.vectors.map(x => x.id).join(" ") === pt.vectors.map(x => x.id).join(" ")) {
                return pt.id;
            }
        }

        return state.pro ? 0 : undefined;
    }

    const templates = useMemo(() => {
        const result : IProjectTemplate[] = [{
            id: 0,
            title: localize("EMPLOYEE_SURVEY"),
            description: localize("EMPLOYEE_SURVEY_DESCRIPTION"),
            icon: "people",
            iconColor: "#787CDD",
            backgroundColor: "#F1F2FC",
            buttonText: localize("CREATE_EMPLOYEE_SURVEY"),
            options: {
                questionModelling: 0,
                surveyInterval: 0,
                purpose: "",
                goal: "",
                isStrategicGoal: false,
                strategicGoal: "",
                strategicGoalChallenges: "",
                vectors: state.projectTypes.length > 0 ? state.projectTypes[0].vectors : []
            }
        }, {
            id: 1,
            title: localize("TRANSFORMATION_PROJECT"),
            description: localize("TRANSFORMATION_PROJECT_DESCRIPTION"),
            icon: "activity",
            iconColor: "#F1AE15",
            backgroundColor: "#FEF8EC",
            buttonText: localize("CREATE_TRANSFORMATION_PROJECT"),
            options: {
                questionModelling: 0,
                surveyInterval: 0,
                vectors: []
            }
        }, {
            id: 2,
            title: localize("SURVEY"),
            description: localize("SURVEY_DESCRIPTION"),
            icon: "task",
            iconColor: "#0EAC00",
            backgroundColor: "#EEF9ED",
            buttonText: localize("CREATE_SURVEY"),
            options: {
                questionModelling: 3,
                surveyInterval: 4,
                purpose: "",
                goal: "",
                isStrategicGoal: false,
                strategicGoal: "",
                strategicGoalChallenges: "",
                vectors: [{
                    name: state.item.name,
                    questions: [],
                    questionStatus: 3
                }]
            }
        }];

        return result;
    }, [state.projectTypes, state.item.name]);

    const canAddVector = () : boolean => {
        return state.pro && state.item.template === 1;
    }

    const canEditVector = (vectorId: number) : boolean => {
        return state.pro && state.item.template === 1 && !vectorId;
    }

    const canAddQuestion = (vectorId: number) : boolean => {
        return (state.pro && !vectorId) || state.item.template === 2;
    }

    const canEditQuestion = (questionId: number) : boolean => {
        return (state.pro || state.item.template === 2) && !questionId;
    }

    if (state.isLoading) {
        return (
            <Loader />
        )
    }

    if (!!state.question) {
        return (
            <QuestionForm
                question={state.question}
                onSave={(item: any) => {
                    if (state.question.isNew) {
                        setState((s: IState) => ({
                            ...s,
                            question: undefined,
                            item: {
                                ...s.item,
                                vectors: [{
                                    ...s.item.vectors[0],
                                    questions: [...s.item.vectors[0].questions, {...item, isNew: false}]
                                }]
                            }
                        }));
                    } else {
                        setState((s: IState) => ({
                            ...s,
                            question: undefined,
                            item: {
                                ...s.item,
                                vectors: [{
                                    ...s.item.vectors[0],
                                    questions: s.item.vectors[0].questions.map((q) => {
                                        if (q.tmpId === state.question.tmpId) {
                                            return item;
                                        }

                                        return q;
                                    })
                                }]
                            }
                        }));
                    }
                }}
                onCancel={() => {
                    setState((s: IState) => ({
                        ...s,
                        question: undefined
                    }));
                }}
            />
        )
    }

    if (state.displayVectorForm) {
        return (
            <VectorForm
                canAddVector={canAddVector}
                canEditVector={canEditVector}
                canAddQuestion={canAddQuestion}
                canEditQuestion={canEditQuestion}
                isCreatingVector={state.isCreatingVector}
                vector={state.activeVectorIndex > -1 ? state.item.vectors[state.activeVectorIndex] : undefined}
                onSave={(vector) => {
                    if (state.isCreatingVector) {
                        setState((s) => ({
                            ...s,
                            displayVectorForm: false,
                            isCreatingVector: false,
                            activeVectorIndex: -1,
                            item: {
                                ...s.item,
                                vectors: [
                                    ...s.item.vectors,
                                    vector
                                ]
                            }
                        }));
                    } else {
                        setState((s) => ({
                            ...s,
                            displayVectorForm: false,
                            isCreatingVector: false,
                            activeVectorIndex: -1,
                            item: {
                                ...s.item,
                                vectors: s.item.vectors.map((v: any, index: number) => {
                                    if (index === state.activeVectorIndex) {
                                        return vector;
                                    } else {
                                        return v;
                                    }
                                })
                            }
                        }));
                    }
                }}
                onCancel={() => {
                    setState((s) => ({
                        ...s,
                        displayVectorForm: false,
                        isCreatingVector: false,
                        activeVectorIndex: -1
                    }));
                }}
            />
        )
    }

    const renderContent = () => {
        if (state.isNew && state.item.template === undefined) {
            return (
                <div className="type-selector">
                    {templates.map((template) => (
                        <div
                            key={template.id}
                            className="type"
                        >
                            <div className="icon" style={{backgroundColor: template.backgroundColor}}>
                                <Visage2Icon icon={template.icon} hexColor={template.iconColor} />
                            </div>
                            <div className="text">
                                <Label weight="medium">{template.title}</Label>
                                <Label size="body-2">{template.description}</Label>
                            </div>
                            <PrimaryButton
                                text={template.buttonText}
                                className="alt-button"
                                onClick={() => {
                                    setState(s => ({
                                        ...s,
                                        item: {
                                            ...s.item,
                                            template: template.id,
                                            ...template.options
                                        }
                                    }));
                                }}
                            />
                        </div>
                    ))}
                </div>
            )
        }

        return (
            <>
            {state.saveError.length > 0 && (
                <ErrorMessagebar
                    errorList={state.saveError}
                    onDismiss={() => {
                        setState(s => ({
                            ...s,
                            saveError: []
                        }));
                    }}
                />
            )}
            <FormSteps
                steps={steps}
                step={state.step}
                onCancelClick={close}
                onSaveClick={save}
                onStepChange={(step: number) => {
                    let isForward = step > state.step;

                    if (isForward) {
                        let isOk = validateStep(state.step);

                        if (!isOk) {
                            return;
                        }

                        if (step === 2 && state.step === 0) {
                            isOk = validateStep(1);
                        }

                        if (!isOk) {
                            return;
                        }

                        if (step === 2 && !state.item.baselineDate) {
                            setState((s) => ({
                                ...s,
                                item: {
                                    ...s.item,
                                    baselineDate: new Date(),
                                },
                            }));
                        }
                    }

                    setState(s => ({
                        ...s,
                        step: step,
                        saveError: []
                    }));
                }}>
                <FormStep step={0}>
                    <FormSection>
                        {props.chooseInstance && (
                            <FormTokenizedObjectInput
                                url={"/api/v1/instances"}
                                label={localize("CUSTOMER")}
                                items={
                                    state.item.instance
                                        ? [state.item.instance]
                                        : []
                                }
                                onChange={(instances) => {
                                    setState((s) => ({
                                        ...s,
                                        item: {
                                            ...state.item,
                                            instance: instances[0],
                                            instanceId: instances[0]?.key,
                                        },
                                    }));
                                }}
                                required
                                itemLimit={1}
                            />
                        )}
                        {state.isNew && (
                            <FormControl>
                                <Dropdown
                                    label={localize("TEMPLATE")}
                                    selectedKey={state.item.template}
                                    required
                                    onChange={(_e, v) => {
                                        const id = Number(v.key);
                                        const template = templates.find(x => x.id === id);

                                        dispatch(setConfirmPopup({
                                            isOpen: true,
                                            title: "",
                                            message: localize("TEMPLATE_CHANGE_INFO"),
                                            onConfirm: async () => {
                                                setState(s => ({
                                                    ...s,
                                                    item: {
                                                        ...s.item,
                                                        template: id,
                                                        ...template.options
                                                    }
                                                }));
                                            },
                                        }));
                                    }}
                                    options={[
                                        ...templates.map((t: any) => {
                                            return {
                                                key: t.id,
                                                text: t.title
                                            }
                                        }),
                                    ]}
                                    styles={{ dropdown: { width: 250 } }}
                                />
                            </FormControl>
                        )}
                        <FormControl>
                            <TextField
                                value={state.item.name}
                                className="textField"
                                label={localize("PROJECT_NAME")}
                                resizable={false}
                                onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, newValue?: string) => {
                                    setState(s => ({
                                        ...s,
                                        item: {
                                            ...s.item,
                                            name: newValue
                                        }
                                    }));
                                }}
                                required
                                aria-required
                                validateOnFocusOut
                                validateOnLoad={false}
                                onGetErrorMessage={validateRequiredTextField}
                            />
                        </FormControl>
                        <div className="form-row">
                            <FormControl>
                                <DatePicker
                                    label={localize("Startdatum")}
                                    firstDayOfWeek={DayOfWeek.Monday}
                                    strings={getDatePickerLanguageStrings()}
                                    formatDate={formatDatePickerDate}
                                    placeholder={localize("ValjDatum")}
                                    ariaLabel={localize("ValjDatum")}
                                    value={state.item.startDate}
                                    isRequired={true}
                                    onSelectDate={(date: Date) => {
                                        setState(s => ({
                                            ...s,
                                            item: {
                                                ...s.item,
                                                startDate: date
                                            }
                                        }));
                                    }}
                                />
                            </FormControl>
                            <FormControl>
                                <DatePicker
                                    label={localize("Slutdatum")}
                                    firstDayOfWeek={DayOfWeek.Monday}
                                    strings={getDatePickerLanguageStrings()}
                                    formatDate={formatDatePickerDate}
                                    placeholder={localize("ValjDatum")}
                                    ariaLabel={localize("ValjDatum")}
                                    value={state.item.endDate}
                                    isRequired={false}
                                    onSelectDate={(date: Date) => {
                                        setState(s => ({
                                            ...s,
                                            item: {
                                                ...s.item,
                                                endDate: date
                                            }
                                        }));
                                    }}
                                />
                            </FormControl>
                        </div>
                        {state.item.template === 1 && (
                            <>
                                <FormControl>
                                    <TextField
                                        value={state.item.purpose}
                                        className="textField"
                                        label={localize("PROJECT_PURPOSE")}
                                        multiline
                                        autoAdjustHeight
                                        resizable={false}
                                        onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, newValue?: string) => {
                                            setState(s => ({
                                                ...s,
                                                item: {
                                                    ...s.item,
                                                    purpose: newValue
                                                }
                                            }));
                                        }}
                                        required={state.pro}
                                        aria-required={state.pro}
                                        validateOnFocusOut
                                        validateOnLoad={false}
                                        onGetErrorMessage={validateRequiredTextField}
                                    />
                                </FormControl>
                                <FormControl>
                                    <TextField
                                        value={state.item.goal}
                                        className="textField"
                                        label={localize("PROJECT_GOAL")}
                                        multiline
                                        autoAdjustHeight
                                        resizable={false}
                                        onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, newValue?: string) => {
                                            setState(s => ({
                                                ...s,
                                                item: {
                                                    ...s.item,
                                                    goal: newValue
                                                }
                                            }));
                                        }}
                                        required={state.pro}
                                        aria-required={state.pro}
                                        validateOnFocusOut
                                        validateOnLoad={false}
                                        onGetErrorMessage={validateRequiredTextField}
                                    />
                                </FormControl>
                                <FormControl label={localize("IS_STRATEGIC_GOAL")}>
                                    <ChoiceGroup
                                        selectedKey={state.item.isStrategicGoal ? "0" : "1"}
                                        options={[
                                            { key: "0", text: localize("YES") },
                                            { key: "1", text: localize("NO") },
                                        ]}
                                        onChange={(e, v) => {
                                            setState(s => ({
                                                ...s,
                                                item: {
                                                    ...s.item,
                                                    isStrategicGoal: v.key === "0"
                                                }
                                            }))
                                        }}
                                        required={true}
                                        aria-required={true}
                                    />
                                </FormControl>
                                {state.item.isStrategicGoal && (
                                    <div>
                                        <FormControl>
                                            <TextField
                                                value={state.item.strategicGoal}
                                                className="textField"
                                                label={localize("STRATEGIC_GOAL")}
                                                multiline
                                                autoAdjustHeight
                                                resizable={false}
                                                onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, newValue?: string) => {
                                                    setState(s => ({
                                                        ...s,
                                                        item: {
                                                            ...s.item,
                                                            strategicGoal: newValue
                                                        }
                                                    }));
                                                }}
                                                required
                                                aria-required
                                                validateOnFocusOut
                                                validateOnLoad={false}
                                                onGetErrorMessage={validateRequiredTextField}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <TextField
                                                value={state.item.strategicGoalChallenges}
                                                className="textField"
                                                label={localize("STRATEGIC_GOAL_CHALLENGES")}
                                                multiline
                                                autoAdjustHeight
                                                resizable={false}
                                                onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, newValue?: string) => {
                                                    setState(s => ({
                                                        ...s,
                                                        item: {
                                                            ...s.item,
                                                            strategicGoalChallenges: newValue
                                                        }
                                                    }));
                                                }}
                                                required
                                                aria-required
                                                validateOnFocusOut
                                                validateOnLoad={false}
                                                onGetErrorMessage={validateRequiredTextField}
                                            />
                                        </FormControl>
                                    </div>
                                )}
                            </>
                        )}
                    </FormSection>
                </FormStep>
                <FormStep step={1}>
                    {state.item.template === 2 && (
                        <>
                            {state.item.vectors[0].questions.length > 0 && (
                                <CompactTable
                                    columNames={[localize("QUESTION")]}
                                    rows={state.item.vectors[0].questions.map((item: any, index: number) => { return [item.text]; })}
                                    buttons={state.item.vectors[0].questions.map((item: any, index: number) => {
                                        if (!canEditQuestion(item.id)) {
                                            return [];
                                        }

                                        return [{
                                            title: localize("Redigera"),
                                            icon: "edit-2",
                                            onClick: () => {
                                                setState(s => ({
                                                    ...s,
                                                    question: item
                                                }));
                                            }
                                        }, {
                                            title: localize("TaBort"),
                                            icon: "trash",
                                            onClick: () => {
                                                setState(s => ({
                                                    ...s,
                                                    item: {
                                                        ...s.item,
                                                        vectors: [{
                                                            ...s.item.vectors[0],
                                                            questions: removeItemAtIndex(state.item.vectors[0].questions, index)
                                                        }]
                                                    }
                                                }))
                                            }
                                        }];
                                    })}
                                />
                            )}
                            <div className="table">
                                {state.item.vectors[0].questions.length === 0 && (
                                    <Label size="body-2" color="p-mid-grey-5" className="no-questions">{localize("NO_QUESTIONS_ERROR")}</Label>
                                )}
                                {canAddQuestion(state.item.vectors[0].id) && (
                                    <div className="table-footer">
                                        <UnstyledButton className="add-button" onClick={() => {
                                            setState(s => ({
                                                ...s,
                                                question: {
                                                    id: 0,
                                                    tmpId: uniqueId(),
                                                    text: "",
                                                    isNew: true,
                                                    questionAlternativeCollectionId: 0
                                                }
                                            }));
                                        }}>
                                            <Visage2Icon icon="add" color="p-green-1" />
                                            <Label
                                                size="body-2"
                                                weight="medium"
                                                color="p-green-1"
                                                fontType="display"
                                            >
                                                {localize("ADD_QUESTION")}
                                            </Label>
                                        </UnstyledButton>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {state.item.template !== 2 && (
                        <FormSection>
                            {state.projectTypes.length > 0 && state.isNew && (
                                <FormControl>
                                    <Dropdown
                                        label={localize("PROJECT_TYPE")}
                                        selectedKey={getProjectType()}
                                        disabled={!state.isNew}
                                        required
                                        placeholder={localize("CHOOSE_PROJECT_TYPE")}
                                        onChange={(_e, v) => {
                                            const id = Number(v.key);

                                            if (id === 0) {
                                                setState(s => ({
                                                    ...s,
                                                    item: {
                                                        ...s.item,
                                                        vectors: []
                                                    }
                                                }));

                                                return;
                                            }

                                            const pt = state.projectTypes.find(x => x.id === id);

                                            setState(s => ({
                                                ...s,
                                                item: {
                                                    ...s.item,
                                                    vectors: pt.vectors
                                                }
                                            }));
                                        }}
                                        options={[
                                            ...state.projectTypes.map((pt: any) => {
                                                return {
                                                    key: pt.id,
                                                    text: pt.name
                                                }
                                            }),
                                            ...(state.pro && state.item.template === 1
                                                ? [
                                                    {
                                                        key: 0,
                                                        text: localize(
                                                            "CUSTOM"
                                                        ),
                                                    },
                                                ]
                                                : []),
                                        ]}
                                        styles={{ dropdown: { width: 250 } }}
                                    />
                                </FormControl>
                            )}
                            {state.item.vectors.length > 0 && (
                                <CompactTable
                                    columNames={[
                                        localize("VECTOR"),
                                        localize("QUESTION_STATUS")
                                    ]}
                                    rows={state.item.vectors.map((item: any) => {
                                        return [
                                            item.name,
                                            !canAddVector() ?
                                                `${item.questions.filter((q) => q.text.length > 0).length} ${localize(item.questions.filter((q) => q.text.length > 0).length === 1 ? "QUESTION" : "QUESTIONS").toLowerCase()}` :
                                                localize("VECTOR_GENERATE_STATUS_" + item.questionStatus)
                                        ]
                                    })}
                                    buttons={state.item.vectors.map((item: any, index: number) => {
                                        const vector = state.item.vectors[index];
                                        const canEdit = canEditVector(vector.id);

                                        return [
                                            ...(!canEdit ? [{
                                                title: localize("Visa"),
                                                icon: "eye",
                                                onClick: () => {
                                                    setState(s => ({
                                                        ...s,
                                                        displayVectorForm: true,
                                                        activeVectorIndex: index,
                                                    }));
                                                }
                                            }] : []),
                                            ...(canEdit ? [{
                                                title: localize("Redigera"),
                                                icon: "edit-2",
                                                onClick: () => {
                                                    setState(s => ({
                                                        ...s,
                                                        displayVectorForm: true,
                                                        activeVectorIndex: index
                                                    }));
                                                }
                                            }, {
                                                title: localize("TaBort"),
                                                icon: "trash",
                                                onClick: () => {
                                                    dispatch(setConfirmPopup({
                                                        isOpen: true,
                                                        title: "",
                                                        message: localize("ArDuSakerPaAttDuVillTaBortDennaPost"),
                                                        onConfirm: async () => {
                                                            setState(s => ({
                                                                ...s,
                                                                item: {
                                                                    ...s.item,
                                                                    vectors: removeItemAtIndex(s.item.vectors, index)
                                                                }
                                                            }));
                                                        },
                                                    }));
                                                }
                                            }] : [])
                                        ];
                                    })}
                                />
                            )}
                            <div className="table">
                                {canAddVector() && (
                                    <div className="table-footer">
                                        <div>
                                            <UnstyledButton
                                                className="add-button"
                                                disabled={state.item.vectors.length >= 6}
                                                onClick={() => {
                                                    setState(s => ({
                                                        ...s,
                                                        displayVectorForm: true,
                                                        isCreatingVector: true
                                                    }));
                                                }}>
                                                <Visage2Icon icon="add" color="p-green-1" />
                                                <Label
                                                    size="body-2"
                                                    weight="medium"
                                                    color="p-green-1"
                                                    fontType="display">{localize("ADD_VECTOR")}</Label>
                                            </UnstyledButton>
                                        </div>
                                        {state.item.vectors.filter(x => x.questionStatus < 3).length > 0 && (
                                            <div>
                                                <Label
                                                    size="body-3"
                                                    color="p-mid-grey-1"
                                                    weight="medium">
                                                    {localize("GENERATE_DESCRIPTION")}
                                                </Label>
                                                <div>
                                                    <PrimaryButton
                                                        disabled={isGeneratingQuestions}
                                                        onClick={() => {
                                                            generateQuestions();
                                                        }}>
                                                        {localize(isGeneratingQuestions ? "VECTOR_GENERATE_STATUS_2" : "GENERATE_QUESTIONS")}
                                                        {isGeneratingQuestions && <AnimatedDots />}
                                                    </PrimaryButton>
                                                </div>
                                                {isGeneratingQuestions && (
                                                    <Loader />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </FormSection>
                    )}
                </FormStep>
                <FormStep step={2}>
                    <FormSection>
                        <FormControl>
                            <FormTokenizedObjectInput
                                items={state.item.targetGroups}
                                types={[SpintrTypes.UberType.User]}
                                label={localize("Malgrupper")}
                                url={"/api/v1/targetgroups"}
                                showAllWhenEmpty
                                onChange={(targetGroups) => {
                                    setState(s => ({
                                        ...s,
                                        item: {
                                            ...s.item,
                                            targetGroups: targetGroups.map((tg) => {
                                                return {
                                                    ...tg,
                                                    id: tg.id ? tg.id : tg.key
                                                }
                                            })
                                        }
                                    }));
                                }}
                            />
                        </FormControl>
                        <FormControl label={localize("RECURRING_SURVEYS")}>
                            <ChoiceGroup
                                selectedKey={state.item.surveyInterval === 4 ? "1" : "0"}
                                options={[
                                    { key: "1", text: localize("NO") },
                                    { key: "0", text: localize("YES") }
                                ]}
                                onChange={(e, v) => {
                                    setState(s => ({
                                        ...s,
                                        item: {
                                            ...s.item,
                                            surveyInterval: v.key === "1" ? 4 : 0
                                        }
                                    }))
                                }}
                                required={true}
                                aria-required={true}
                            />
                        </FormControl>
                        {state.item.surveyInterval !== 4 && (
                            <>
                                <FormControl label={localize("SURVEY_INTERVAL")}>
                                    <ChoiceGroup
                                        selectedKey={state.item.surveyInterval.toString()}
                                        options={[
                                            { key: "0", text: localize("EVERY_MONTH") },
                                            { key: "1", text: "2 " + localize("MONTHS") },
                                            { key: "2", text: "3 " + localize("MONTHS") },
                                            { key: "3", text: localize("DAYS") },
                                        ]}
                                        onChange={(e, v) => {
                                            setState(s => ({
                                                ...s,
                                                item: {
                                                    ...s.item,
                                                    surveyInterval: Number(v.key)
                                                }
                                            }))
                                        }}
                                        required={true}
                                        aria-required={true}
                                    />
                                </FormControl>
                                {state.item.surveyInterval === 3 && (
                                    <FormControl>
                                        <SpinButton
                                            className="spinButton"
                                            defaultValue={state.item.surveyIntervalFreeChoiceDays.toString()}
                                            min={1}
                                            step={1}
                                            onChange={(event: React.SyntheticEvent<HTMLElement>, newValue?: string) => {
                                                setState(s => ({
                                                    ...s,
                                                    item: {
                                                        ...s.item,
                                                        surveyIntervalFreeChoiceDays: Number(newValue)
                                                    }
                                                }))
                                            }}
                                        />
                                    </FormControl>
                                )}
                                <FormControl>
                                    <div className="form-row">
                                    <DatePicker
                                        label={localize("BASELINE_DATE")}
                                        firstDayOfWeek={DayOfWeek.Monday}
                                        strings={getDatePickerLanguageStrings()}
                                        formatDate={formatDatePickerDate}
                                        placeholder={localize("ValjDatum")}
                                        ariaLabel={localize("ValjDatum")}
                                        value={state.item.baselineDate}
                                        isRequired={true}
                                        onSelectDate={(date: Date) => {
                                            setState(s => ({
                                                ...s,
                                                item: {
                                                    ...s.item,
                                                    baselineDate: date
                                                }
                                            }));
                                        }}
                                    />
                                    {/* TimePicker doesn't seem to re-render properly, this should force it to re-render once baselineDate is set */}
                                    {state.item.baselineDate &&
                                        <TimePicker
                                            label={localize("ValjTid")}
                                            placeholder={localize("ValjTid")}
                                            ariaLabel={localize("ValjTid")}
                                            increments={15}
                                            allowFreeform
                                            dateAnchor={state.item.baselineDate}
                                            value={state.item.baselineDate}
                                            timeRange={{start: 0, end: 24}}
                                            onChange={(ev, time) => {
                                                setState(s => ({
                                                    ...s,
                                                    item: {
                                                        ...s.item,
                                                        baselineDate: time,
                                                    }
                                                }));
                                            }}
                                        />
                                    }
                                    </div>
                                </FormControl>
                            </>
                        )}
                        {state.isNew && state.item.template !== 2 && (
                            <FormControl label={localize("QUESTION_MODELLING")}>
                                <ChoiceGroup
                                    selectedKey={state.item.questionModelling.toString()}
                                    options={[
                                        { key: "0", text: localize("STRAIGHT") },
                                        { key: "1", text: localize("PARTLY_RANDOM") },
                                        { key: "2", text: localize("RANDOM") },
                                        { key: "3", text: localize("ALL")}
                                    ]}
                                    onChange={(e, v) => {
                                        setState(s => ({
                                            ...s,
                                            item: {
                                                ...s.item,
                                                questionModelling: Number(v.key)
                                            }
                                        }))
                                    }}
                                    required={true}
                                    aria-required={true}
                                />
                            </FormControl>
                        )}
                        <FormControl label={localize("ANON_SURVEYS")}>
                            <ChoiceGroup
                                selectedKey={state.item.isAnonymous ? "1" : "0"}
                                options={[
                                    { key: "0", text: localize("NO") },
                                    { key: "1", text: localize("YES") }
                                ]}
                                onChange={(e, v) => {
                                    setState(s => ({
                                        ...s,
                                        item: {
                                            ...s.item,
                                            isAnonymous: v.key === "1"
                                        }
                                    }))
                                }}
                                required={true}
                                aria-required={true}
                            />
                        </FormControl>
                        <FormControl label={localize("END_QUESTION")}>
                            <ChoiceGroup
                                selectedKey={state.item.hasEndQuestion ? "1" : "0"}
                                options={[
                                    { key: "0", text: localize("NO") },
                                    { key: "1", text: localize("YES") }
                                ]}
                                onChange={(e, v) => {
                                    setState((s) => ({
                                        ...s,
                                        item: {
                                            ...s.item,
                                            hasEndQuestion: v.key === "1"
                                        }
                                    }))
                                }}
                                required={true}
                                aria-required={true}
                            />
                        </FormControl>
                        {
                            state.item.hasEndQuestion && <FormControl>
                                <TextField
                                    value={state.item.endQuestionText}
                                    placeholder={localize("END_QUESTION_PLACEHOLDER")}
                                    onChange={(e, v) => {
                                    setState((s) => ({
                                        ...s,
                                        item: {
                                            ...s.item,
                                            endQuestionText: v,
                                        }
                                    }));
                                }} />
                            </FormControl>
                        }
                        <FormControl label={localize("END_ENPS_QUESTION_LABEL") + " (" + localize("END_ENPS_QUESTION") + ")"}>
                            <ChoiceGroup
                                selectedKey={state.item.hasENPSQuestion ? "1" : "0"}
                                options={[
                                    { key: "0", text: localize("NO") },
                                    { key: "1", text: localize("YES") }
                                ]}
                                onChange={(e, v) => {
                                    setState((s) => ({
                                        ...s,
                                        item: {
                                            ...s.item,
                                            hasENPSQuestion: v.key === "1"
                                        }
                                    }))
                                }}
                                required={true}
                                aria-required={true}
                            />
                        </FormControl>
                    </FormSection>
                </FormStep>
            </FormSteps>
            </>
        )
    }

    return (
        <div className="ProjectForm">
            <PhoenixForm data={state.item}>
            <PopupHeader
                text={state.isNew ? localize("START_NEW_PROJECT") : localize("EDIT_PROJECT")}
                renderFooterContent={state.isNew ? undefined : () => {
                    return (
                        <ProjectStatusSelector
                            projectId={state.item.id}
                            project={state.item}
                            onStatusChange={(status: number) => {
                                setState(s => ({
                                    ...s,
                                    item: {
                                        ...s.item,
                                        status
                                    }
                                }));
                            }} />
                    )
                }}
                onClose={() => {
                    close();
                }} />
                {renderContent()}
            </PhoenixForm>
        </div>
    )
}

const mapStateToProps = (state: IApplicationState, props: IProps) => ({
    ...props,
    instancePro: state.instance.currentInstance.pro,
});

export default connect(mapStateToProps)(ProjectForm);
